import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useRouter,
} from '@tanstack/react-router'
import {
  Building2Icon,
  FileTextIcon,
  FolderOpenIcon,
  HomeIcon,
  LogOutIcon,
  ScaleIcon,
  SettingsIcon,
} from 'lucide-react'

import {
  Layout,
  LoadingScreen,
  MainContent,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
} from '@/components'
import {
  type Account,
  AccountBillingUsage,
  ManageSubscriptionButton,
  useAccount,
  useFetchAccount,
  useFetchCurrentUser,
  useIsTrialAccount,
  useLoadingAccount,
} from '@/modules/account'
import {
  requireSession,
  useAuthActions,
  useAuthMutations,
  useAuthStateChange,
  useUser,
} from '@/modules/auth'
import {
  GettingStartedBar,
  GettingStartedNavItem,
  GettingStartedWelcomeDialog,
  useShowGettingStartedBar,
} from '@/modules/getting-started'
import { AskSurface, GetHelpNavItem } from '@/modules/help'
import {
  SupportDeepLink,
  SupportRealtime,
  SupportTicketDialog,
} from '@/modules/support'
import { UpdatesNavItem } from '@/modules/updates'
import { WebsocketProvider } from '@/modules/websocket'
import { cn } from '@/utils'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: requireSession,
  component: AuthenticatedLayoutComponent,
})

function AuthenticatedLayoutComponent() {
  const account = useAccount() as Account
  const location = useLocation()
  const isLoadingAccount = useLoadingAccount()
  const router = useRouter()
  const user = useUser()
  const { signOut } = useAuthMutations()
  const showTourBar = useShowGettingStartedBar()
  const isTrial = useIsTrialAccount()
  const { clearSession } = useAuthActions()
  useFetchAccount()
  useFetchCurrentUser()

  // Never sign out from the listener: every signOut emits SIGNED_OUT again.
  const handleSignOut = () => signOut()

  useAuthStateChange({
    onSignedOut: () => {
      clearSession()
      router.navigate({ to: '/login' })
    },
  })

  if (isLoadingAccount) {
    return <LoadingScreen />
  }

  return (
    <>
      <WebsocketProvider>
        <Layout>
          <Sidebar>
            <SidebarHeader>
              <Link to="/">
                <img
                  src="/logo.svg"
                  alt="Counsel Pro"
                  className="w-36 h-auto"
                />
              </Link>
            </SidebarHeader>

            <SidebarContent className="px-2 space-y-1">
              {!isTrial && (
                <SidebarItem
                  asChild
                  active={location.pathname === '/'}
                  className=""
                  icon={HomeIcon}
                >
                  <Link to="/" preload="intent">
                    Dashboard
                  </Link>
                </SidebarItem>
              )}
              <SidebarItem
                asChild
                active={location.pathname.startsWith('/projects')}
                icon={FolderOpenIcon}
              >
                <Link to="/projects" preload="intent">
                  Projects
                </Link>
              </SidebarItem>
              {!isTrial && account?.isDocumentsEnabled && (
                <SidebarItem
                  asChild
                  active={location.pathname.startsWith('/documents')}
                  icon={FileTextIcon}
                >
                  <Link to="/documents" preload="intent">
                    Documents
                  </Link>
                </SidebarItem>
              )}
              {!isTrial && account?.isToolsBankruptcyEnabled && (
                <div className="mt-2">
                  <p className="px-3 pb-1 text-xs font-medium text-muted-foreground">
                    Tools
                  </p>
                  <SidebarItem
                    asChild
                    active={location.pathname.startsWith(
                      '/tools/bankruptcy-screener',
                    )}
                    icon={ScaleIcon}
                  >
                    <Link to="/tools/bankruptcy-screener" preload="intent">
                      Bankruptcy Screener
                    </Link>
                  </SidebarItem>
                </div>
              )}
            </SidebarContent>

            <div className="p-4">
              <AccountBillingUsage />
            </div>

            <SidebarFooter>
              {account?.isAssumed && (
                <div className="rounded-lg border border-pink-300 p-2 bg-barberpole-pink-50 animate-barberpole mb-4 flex items-start gap-3 shadow">
                  <div className="mt-0.5 rounded-md bg-pink-200 p-1.5 text-pink-700">
                    <Building2Icon />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Assuming account</p>
                    <p className="text-xs text-muted-foreground text-pretty">
                      {account?.name}
                    </p>
                    <p className="text-xs text-muted-foreground text-pretty">
                      ID: {account?.id}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
                <div className="h-8 w-8 rounded-full bg-blue-2 flex items-center justify-center text-foreground font-bold shrink-0">
                  {user?.user_metadata.initials}
                </div>
                <div className="flex flex-col truncate">
                  <span
                    className="font-medium text-foreground text-ellipsis overflow-hidden whitespace-normal"
                    title={user?.user_metadata.displayName}
                  >
                    {user?.user_metadata.displayName}
                  </span>
                  <span className="text-xs truncate" title={user?.email}>
                    {user?.email}
                  </span>
                </div>
              </div>

              <GettingStartedNavItem />
              {!isTrial && <UpdatesNavItem />}
              <SidebarItem
                asChild
                active={location.pathname.startsWith('/settings')}
                icon={SettingsIcon}
              >
                <Link to="/settings" preload="intent">
                  Settings
                </Link>
              </SidebarItem>
              {!isTrial && <ManageSubscriptionButton />}
              <GetHelpNavItem />
              <SidebarItem onClick={handleSignOut} icon={LogOutIcon}>
                Log out
              </SidebarItem>
            </SidebarFooter>
          </Sidebar>

          <div className="relative flex min-w-0 flex-1 flex-col">
            <MainContent className={cn('min-h-0', showTourBar && 'pb-28')}>
              <Outlet />
            </MainContent>
            <GettingStartedBar />
          </div>
          <div id="right-rail" className="flex shrink-0" />
        </Layout>
      </WebsocketProvider>
      <SupportDeepLink />
      <GettingStartedWelcomeDialog />
      <SupportRealtime />
      <SupportTicketDialog />
      <AskSurface />
    </>
  )
}
